import ResponsiveImage from "@/components/Shared/ResponsiveImage";
import type { ReactNode } from "react";
import ServiceBreadcrumbs from "./ServiceBreadcrumbs";

type ServiceHeroHeaderProps = {
  imageSrc: string;
  imageAlt: string;
  breadcrumbItems: { label: string; href?: string }[];
  badge?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
};

export default function ServiceHeroHeader({
  imageSrc,
  imageAlt,
  breadcrumbItems,
  badge,
  title,
  description,
  actions,
}: ServiceHeroHeaderProps) {

  return (
    <section className="relative min-h-[35vh] md:min-h-[65vh] flex items-center pt-6 pb-10 md:pt-8 md:pb-12 overflow-hidden bg-primary">
      <div className="absolute inset-0 z-0">
        <ResponsiveImage
          src={imageSrc}
          alt={imageAlt}
          fill
          className="h-full w-full object-cover md:object-center opacity-40 md:opacity-100 transition-opacity duration-700"
          sizes="100vw"
          priority
        />
        <div className="absolute inset-0 bg-linear-to-b from-primary/90 via-primary/40 to-primary md:bg-linear-to-r md:from-primary/95 md:via-primary/50 md:to-transparent" />
      </div>

      <div className="relative container mx-auto px-6 z-10">
        <div className="max-w-3xl">
          {/* Breadcrumbs & Badge compact group */}
          <div className="flex flex-col gap-1 mb-3 opacity-90">
            <ServiceBreadcrumbs className="scale-90 origin-left" items={breadcrumbItems} />
            {badge && (
              <div className="flex items-center gap-2">
                <span className="h-px w-4 bg-accent/40" />
                <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-accent">{badge}</span>
              </div>
            )}
          </div>

          <h1 className="mb-3 text-3xl xs:text-4xl md:text-6xl lg:text-7xl font-display font-bold text-primary-foreground leading-[1.1] tracking-tight">
            {title}
          </h1>

          {description && (
            <p className="mb-4 text-[13px] xs:text-sm md:text-xl font-serif italic text-primary-foreground/80 leading-relaxed max-w-xl border-l-2 border-accent/20 pl-5">
              {description}
            </p>
          )}

          {actions && (
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              {actions}
            </div>
          )}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-linear-to-t from-background to-transparent z-0" />
    </section>
  );
}
